//* Libraries imports
import { useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container } from "react-bootstrap";

//* Hooks imports
import { useGetResult } from "../../../../hooks/queries/result/use-get-result";

//* Components imports
import { ViewResultStudentInfo } from "./view-result-student-info";
import { ViewResultCardQuestion } from "./view-result-card-question";
import { ErrorMessages } from "../../../../component/common/ErrorMessages";
import { ResultViewSkeleton } from "../../../../skeleton/ResultViewSkeleton/result-view-skeleton";

//* Types imports
import { FullResult } from "../../../../schemas/common/result/result";

export function Content() {
  const { resultId } = useParams<{ resultId: string }>();
  const navigate = useNavigate();

  const { data: result, isPending, error } = useGetResult(Number(resultId));
  const respostas = useMemo(() => {
    if (!result?.data?.respostas) return {};
    try {
      return JSON.parse(result.data.respostas);
    } catch {
      return {};
    }
  }, [result?.data?.respostas]);

  const getAnswerForQuestion = useMemo(() => {
    return (questao: FullResult["resultado"]["questoes"][0]) => {
      if (questao.type_questao === "dissertativa") {
        return questao.resposta_estudante || null;
      }
      return respostas[questao.id] || null;
    };
  }, [respostas]);

  const questionsWithNumbers = useMemo(() => {
    if (!result?.data?.questoes) return [];

    let questionNumber = 0;
    const processedQuestions: Array<{
      questao: FullResult["resultado"]["questoes"][0];
      questionNumber: number;
    }> = [];

    result.data.questoes.forEach(
      (questao: FullResult["resultado"]["questoes"][0]) => {
        if (
          questao.type_questao === "simples" ||
          questao.type_questao === "dissertativa"
        ) {
          questionNumber++;
          processedQuestions.push({ questao, questionNumber });
        } else if (questao.type_questao === "conjunto") {
          questionNumber++;
          processedQuestions.push({ questao, questionNumber });
        }
      }
    );

    return processedQuestions;
  }, [result?.data?.questoes]);
  const handleBack = () => {
    navigate(-1);
  };

  const handleScrollToTop = useCallback(() => {
    const topAnchor = document.getElementById("top-anchor");
    topAnchor?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const hasCertificate = useMemo(() => {
    return Boolean(result?.data?.estudante?.certificado);
  }, [result?.data?.estudante?.certificado]);

  if (isPending) {
    return <ResultViewSkeleton showCertificate={hasCertificate} />;
  }

  if (error || !result?.success || !result?.data) {
    return (
      <Container className="py-5">
        <ErrorMessages
          messages={["Não foi possível carregar os detalhes do resultado."]}
          clearErrors={handleBack}
        />
        <div className="text-center mt-4">
          <button className="btn btn-outline-secondary" onClick={handleBack}>
            Voltar
          </button>
        </div>
      </Container>
    );
  }
  const { estudante, desempenho_prova } = result!.data!;
  return (
    <Container className="p-4">
      <div id="top-anchor"></div>

      <ViewResultStudentInfo
        estudante={estudante}
        desempenho_prova={desempenho_prova}
        certificado={estudante.certificado}
        questionsWithNumbers={questionsWithNumbers}
        onBack={handleBack}
        notas={result?.data?.notas}
      />

      <div className="questions-container p-0 mt-4">
        {questionsWithNumbers.map(({ questao, questionNumber }) => (
          <ViewResultCardQuestion
            key={questao.id}
            questao={questao}
            questionNumber={questionNumber}
            userAnswer={getAnswerForQuestion(questao)}
            showAnswers={true}
            examId={Number(resultId)}
          />
        ))}

        <div className="text-center mt-5 pt-4">
          <button
            className="btn btn-primary btn-lg rounded-pill shadow-sm px-4 py-2"
            onClick={handleScrollToTop}
            type="button"
          >
            <i className="fas fa-arrow-up me-2"></i>
            Voltar ao Topo
          </button>
        </div>
      </div>
    </Container>
  );
}
